import type { CellContext } from '@tanstack/react-table'

import { useCheckout } from '@/modules/account'
import type { Project } from '@/modules/projects'
import {
  daysUntilExpiration,
  type ExpirationState,
  getExpirationState,
} from '@/modules/projects/expiration'
import { cn, humanizeDate } from '@/utils'

const RenewLink = ({ slug }: { slug: string }) => {
  const { renewProject } = useCheckout()

  return (
    <button
      type="button"
      onClick={() => renewProject(slug)}
      className="text-xs font-medium hover:underline cursor-pointer"
    >
      Renew
    </button>
  )
}

const BADGE_STYLES: Record<'expiringSoon' | 'expired', string> = {
  expiringSoon: 'text-amber-700 border-amber-200',
  expired: 'text-red-700 border-red-200',
}

const expirationLabel = (project: Project, state: ExpirationState) => {
  if (state === 'expired') return 'Expired'

  const days = daysUntilExpiration(project)
  if (days <= 0) return 'Expires today'
  if (days === 1) return 'Expires tomorrow'
  return `Expires in ${days} days`
}

const ExpirationBadge = ({
  state,
  label,
}: {
  state: 'expiringSoon' | 'expired'
  label: string
}) => (
  <span
    className={cn(
      'inline-flex items-center text-xs font-medium',
      BADGE_STYLES[state],
    )}
  >
    {label}
  </span>
)

// Shows the expiration date, and for a single-project plan that is expired or
// close to it, surfaces the state and an inline renewal. Meaning is carried by
// text ("Expired" / "Expires in N days"), not color alone.
export const ExpirationCell = (cell: CellContext<Project, string>) => {
  const project = cell.row.original
  const formattedDate = humanizeDate(project.expiresAt)
  const state = getExpirationState(project)

  if (!formattedDate) return <span className="text-muted-foreground">—</span>

  if (state === 'active')
    return <span className="text-sm">{formattedDate}</span>

  return (
    <div className="flex flex-col">
      <span className="text-sm">{formattedDate}</span>
      <div className="flex items-center gap-2">
        <ExpirationBadge
          state={state}
          label={expirationLabel(project, state)}
        />{' '}
        · <RenewLink slug={project.slug} />
      </div>
    </div>
  )
}
